import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Download, Loader2, CheckCircle2, AlertTriangle, ArrowUpDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { AccessDenied } from '@/components/ui/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useUnits } from '@/hooks/api/useProperties';
import { formatMoney } from '@/lib/money';
import { showToast } from '@/lib/toast';
import { extractErrorMessage } from '@/lib/error';
import { ParityBadge, parityStateLabel } from '@/components/channels/parityState';
import { useSyncSnapshot, useVerifySync, usePushSyncCell } from '@/hooks/api/useChannelSync';
import {
  connectedOtasFromSnapshot,
  downloadSyncExport,
  type SyncCell,
  type SyncMetric,
} from '@/services/api/channelSync.api';

const STATE_OPTIONS = [
  { value: '', label: 'All states' },
  { value: 'external_change', label: 'Changed on AxisRooms' },
  { value: 'push_unconfirmed', label: 'Not confirmed' },
  { value: 'ota_divergence', label: 'OTAs differ' },
  { value: 'closed', label: 'Closed' },
  { value: 'sync_failed', label: "Couldn't verify" },
  { value: 'synced', label: 'Synced' },
];

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function RateParityReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const propertyId = useSelectedPropertyId();
  const [searchParams, setSearchParams] = useSearchParams();

  const canAccess = user?.role === UserRole.ADMIN || user?.role === UserRole.REVENUE_MANAGER;

  const metric = (searchParams.get('metric') as SyncMetric) || 'rate';
  const stateFilter = searchParams.get('state') ?? '';
  const otaFilter = searchParams.get('ota') ?? '';
  const roomFilter = searchParams.get('room') ?? '';
  const [sortByDelta, setSortByDelta] = useState(false);

  const snapshotQuery = useSyncSnapshot(
    { propertyId, metric, ...(otaFilter ? { otaId: otaFilter } : {}), ...(roomFilter ? { roomId: roomFilter } : {}) },
    !!propertyId && canAccess,
  );
  const verify = useVerifySync();
  const pushCell = usePushSyncCell();
  const { data: unitsData } = useUnits(propertyId || undefined);

  const unitNames = useMemo(() => {
    const raw = (unitsData as { data?: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }> | undefined);
    const list = Array.isArray(raw) ? raw : raw?.data ?? [];
    return new Map(list.map((u) => [u.id, u.name]));
  }, [unitsData]);

  const snapshot = snapshotQuery.data;
  const cells = useMemo(() => snapshot?.cells ?? [], [snapshot]);
  const otas = useMemo(() => connectedOtasFromSnapshot(cells), [cells]);
  const rooms = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cells) if (!m.has(c.externalListingId)) m.set(c.externalListingId, unitNames.get(c.unitId) ?? c.externalListingId);
    return [...m.entries()];
  }, [cells, unitNames]);

  const rows = useMemo(() => {
    let list = cells.slice();
    if (stateFilter) list = list.filter((c) => c.parityState === stateFilter);
    if (sortByDelta) {
      list = list.sort((a, b) => Math.abs(delta(b)) - Math.abs(delta(a)));
    } else {
      list = list.sort((a, b) => a.date.localeCompare(b.date) || a.externalListingId.localeCompare(b.externalListingId) || a.otaId.localeCompare(b.otaId));
    }
    return list;
  }, [cells, stateFilter, sortByDelta]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function runVerify() {
    verify.mutate(
      { propertyId, ...(otaFilter ? { otaId: otaFilter } : {}), ...(roomFilter ? { roomId: roomFilter } : {}) },
      {
        onSuccess: (res) => showToast(`Synced — ${res.summary.externalChange} changed on AxisRooms`),
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  }

  async function exportCsv() {
    try {
      await downloadSyncExport({ propertyId, metric, ...(otaFilter ? { otaId: otaFilter } : {}), ...(roomFilter ? { roomId: roomFilter } : {}) });
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }

  function retryPush(cell: SyncCell) {
    const to = format(new Date(parseISO(cell.date).getTime() + 86_400_000), 'yyyy-MM-dd');
    const value = metric === 'rate' ? cell.pmsLastKnownRate ?? cell.otaLoadedRate ?? 0 : cell.closed ? 0 : Math.max(1, cell.otaLoadedFree ?? 1);
    pushCell.mutate(
      { propertyId, unitId: cell.unitId, rateplanId: cell.rateplanId, metric, from: cell.date, to, value },
      {
        onSuccess: () => showToast('Re-pushed to AxisRooms — pending confirmation.'),
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  }

  if (!canAccess) {
    return <AccessDenied message="AxisRooms Sync is restricted to Admins and Revenue Managers." />;
  }

  const lastSynced = snapshot?.lastSyncedAt ?? null;

  return (
    <div>
      <PageHeader
        title="AxisRooms Sync"
        description="Rate & availability parity with AxisRooms across your rooms and OTAs."
        actions={
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 hidden sm:inline">Synced {fmtRelative(lastSynced)}</span>
            <button
              type="button"
              onClick={runVerify}
              disabled={verify.isPending || !propertyId}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={15} className={verify.isPending ? 'animate-spin' : ''} aria-hidden="true" />
              {verify.isPending ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!propertyId || cells.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <Download size={15} aria-hidden="true" /> Export CSV
            </button>
          </div>
        }
      />

      {!propertyId ? (
        <EmptyState icon={AlertTriangle} title="Select a property" description="Choose a single property to view its AxisRooms parity." />
      ) : (
        <>
          {/* Summary strip */}
          {snapshot && (
            <div className="mb-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: 'Changed on AxisRooms', n: snapshot.summary.externalChange },
                { label: 'Pushes unconfirmed', n: snapshot.summary.pushUnconfirmed },
                { label: 'OTA divergences', n: snapshot.summary.divergence },
                { label: 'Rooms closed', n: snapshot.summary.closed },
                { label: 'Sync errors', n: snapshot.summary.syncErrors },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <div className={cn('text-2xl font-semibold', s.n > 0 ? 'text-gray-900' : 'text-gray-300')}>{s.n}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Metric">
              {(['rate', 'availability'] as SyncMetric[]).map((m) => (
                <button key={m} type="button" onClick={() => setParam('metric', m)} aria-pressed={metric === m}
                  className={cn('px-3 py-1.5 text-xs font-medium capitalize', metric === m ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                  {m}
                </button>
              ))}
            </div>
            <select value={stateFilter} onChange={(e) => setParam('state', e.target.value)} aria-label="Filter by state"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              {STATE_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            <select value={otaFilter} onChange={(e) => setParam('ota', e.target.value)} aria-label="Filter by OTA"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">All OTAs</option>
              {otas.map((o) => (<option key={o.otaId} value={o.otaId}>{o.otaName}</option>))}
            </select>
            <select value={roomFilter} onChange={(e) => setParam('room', e.target.value)} aria-label="Filter by room"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">All rooms</option>
              {rooms.map(([id, name]) => (<option key={id} value={id}>{name}</option>))}
            </select>
          </div>

          {/* Table / states */}
          {snapshotQuery.isLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-primary-500" aria-label="Loading" /></div>
          ) : snapshotQuery.isError ? (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
              Couldn&apos;t reach AxisRooms — showing the last snapshot. <button type="button" onClick={() => void snapshotQuery.refetch()} className="font-semibold underline">Retry</button>
            </div>
          ) : cells.length === 0 ? (
            <EmptyState icon={RefreshCw} title="Not synced yet" description="No AxisRooms values mirrored for this property. Run a sync to pull the current rates and availability." actionLabel="Sync now" onAction={runVerify} />
          ) : rows.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="Everything matches AxisRooms" description={`No cells in this filter as of ${fmtRelative(lastSynced)}.`} />
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100" aria-label="Parity report">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Date', 'Room', 'OTA', 'PMS last-known', 'Live AxisRooms'].map((c) => (
                        <th key={c} scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{c}</th>
                      ))}
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <button type="button" onClick={() => setSortByDelta((v) => !v)} className="inline-flex items-center gap-1 hover:text-gray-700">
                          Δ <ArrowUpDown size={11} aria-hidden="true" />
                        </button>
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">State</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Synced</th>
                      <th scope="col" className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((c) => {
                      const d = delta(c);
                      const rowLabel = `${format(parseISO(c.date), 'MMM d')} ${unitNames.get(c.unitId) ?? c.externalListingId} ${c.otaName ?? c.otaId} ${parityStateLabel(c.parityState)}`;
                      return (
                        <tr
                          key={`${c.externalListingId}:${c.otaId}:${c.rateplanId}:${c.date}`}
                          tabIndex={0}
                          role="button"
                          aria-label={rowLabel}
                          onClick={() => navigate(`/channel-manager?tab=sync&room=${c.externalListingId}&ota=${c.otaId}`)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/channel-manager?tab=sync&room=${c.externalListingId}&ota=${c.otaId}`); } }}
                          className="hover:bg-gray-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400"
                        >
                          <td className="px-4 py-2.5 text-sm text-gray-700 whitespace-nowrap">{format(parseISO(c.date), 'MMM d, yyyy')}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-800">{unitNames.get(c.unitId) ?? c.externalListingId}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{c.otaName ?? c.otaId}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{metricVal(c, metric, 'pms')}</td>
                          <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{metricVal(c, metric, 'ota')}</td>
                          <td className={cn('px-4 py-2.5 text-sm font-medium', d > 0 ? 'text-green-600' : d < 0 ? 'text-red-600' : 'text-gray-400')}>
                            {metric === 'rate' && Number.isFinite(d) && d !== 0 ? `${d > 0 ? '+' : ''}${formatMoney(d)}` : '—'}
                          </td>
                          <td className="px-4 py-2.5"><ParityBadge state={c.parityState} size="sm" /></td>
                          <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">{fmtRelative(c.lastSyncedAt)}</td>
                          <td className="px-4 py-2.5 text-right">
                            {c.parityState === 'push_unconfirmed' && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); retryPush(c); }}
                                disabled={pushCell.isPending}
                                className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              >
                                <RefreshCw size={11} aria-hidden="true" /> Retry push
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

function delta(c: SyncCell): number {
  if (c.otaLoadedRate == null || c.pmsLastKnownRate == null) return 0;
  return c.otaLoadedRate - c.pmsLastKnownRate;
}

function metricVal(c: SyncCell, metric: SyncMetric, side: 'ota' | 'pms'): string {
  if (metric === 'availability') {
    if (side === 'ota') return c.closed ? 'Closed' : c.otaLoadedFree != null ? `${c.otaLoadedFree} free` : '—';
    return c.pmsLastKnownFree != null ? `${c.pmsLastKnownFree} free` : '—';
  }
  const v = side === 'ota' ? c.otaLoadedRate : c.pmsLastKnownRate;
  return v != null ? formatMoney(v) : '—';
}
